import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Lock, Vote, Network, ReceiptText, ScrollText, FileSearch, Landmark, Mail, Languages } from "lucide-react";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useLandingLanguageToggle } from "./Header";
import { Container, Counter, IconTile, LiveDot, Reducers, Reveal, SectionHeading, SmartLink, STATS_THRESHOLDS, useCopy, useStats } from "./shared";

/* ---------------------------------- trust ---------------------------------- */

const TRUST_ICONS = [Network, Vote, Lock, ReceiptText];
const CHIP_ICONS = { charter: ScrollText, ledger: Network, controls: ShieldCheck };
/* Plain-language entry kinds (committee R-2): the internal ledger types never appear on the public page. */
const KIND_TONE = { record: "status-active", allocate: "status-active", valuation: "status-pending", distribution: "status-active", transfer: "border-border bg-info-soft text-info", retire: "status-redeemed" };

function LedgerPreview({ title, rows, verified }) {
  return (
    <div className="lp-card rounded-2xl p-5" data-testid="landing-ledger-visual">
      <div className="flex items-center justify-between"><div className="text-sm font-semibold">{title}</div><FileSearch size={16} className="text-muted-foreground" /></div>
      <ol className="mt-4 space-y-0">
        {rows.map((r, i) => (
          <Reveal as="li" key={r.kind} delay={i * 0.08} className={cn("relative flex gap-3 pb-4 last:pb-0", i >= 4 && "hidden sm:flex")} data-testid={`landing-ledger-row-${r.kind}`}>
            {i < rows.length - 1 && <span className={cn("absolute start-[13px] top-7 h-[calc(100%-1.25rem)] w-px bg-primary/40", i === 3 && "hidden sm:block")} aria-hidden />}
            <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-2 font-mono text-[10px] text-muted-foreground" dir="ltr">#{16 + i * 2}</span>
            <div className="min-w-0 flex-1 rounded-xl border border-border bg-surface-2 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2"><span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold", KIND_TONE[r.kind])}>{r.tag}</span><ShieldCheck size={12} className="text-success" aria-hidden /></div>
              <div className="mt-1 text-xs">{r.label}</div>
            </div>
          </Reveal>
        ))}
      </ol>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-success-border bg-success-soft px-3 py-2 text-xs font-semibold text-success" data-testid="landing-ledger-verified"><ShieldCheck size={14} />{verified}</div>
    </div>
  );
}

/** Trust & assurance, merged (committee R-9): charter / audit / role chips, four outcome cards and the de-tokenised ledger preview. */
export function TrustSection() {
  const { c } = useCopy();
  return (
    <section id="trust" className="scroll-mt-20 py-16 sm:py-20" data-testid="landing-trust-section">
      <Container>
        <Reveal>
          <SectionHeading eyebrow={c.trust.eyebrow} title={c.trust.title} subtitle={c.trust.subtitle} />
          <ul className="mt-6 flex flex-wrap gap-2" data-testid="landing-assurance-chips">
            {c.trust.chips.map((chip) => { const I = CHIP_ICONS[chip.k]; return <li key={chip.k} className="lp-card inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium" data-testid={`landing-assurance-chip-${chip.k}`}><I size={14} className="text-primary" strokeWidth={1.75} />{chip.label}</li>; })}
          </ul>
        </Reveal>
        <div className="mt-10 grid gap-4 lg:grid-cols-5 lg:gap-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-3 lg:gap-5">
            {c.trust.cards.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.06} className="h-full">
                <div className="lp-card flex h-full flex-col rounded-2xl p-5" data-testid={`landing-trust-card-${i + 1}`}>
                  <IconTile icon={TRUST_ICONS[i]} className="h-11 w-11" size={20} />
                  <h3 className="mt-4 text-base font-semibold">{card.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="lg:col-span-2"><LedgerPreview title={c.trust.ledgerTitle} rows={c.trust.ledgerRows} verified={c.trust.verified} /></Reveal>
        </div>
      </Container>
    </section>
  );
}

/* ---------------------------------- stats ---------------------------------- */

/** Live figures from the audit record — rendered only past the scale thresholds (committee R-3); nothing shows before that. */
export function StatsStrip() {
  const { c, ar } = useCopy();
  const { data } = useStats();
  const atScale = Boolean(data) && Number(data.pools_total) >= STATS_THRESHOLDS.pools_total && Number(data.owners) >= STATS_THRESHOLDS.owners;
  if (!atScale) return null;
  return (
    <section className="border-y border-border bg-surface-2 py-12" data-testid="landing-stats-section">
      <Container>
        <div className="lp-eyebrow mb-6 inline-flex items-center gap-2 text-muted-foreground"><LiveDot />{c.stats.eyebrow}</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {c.stats.items.map((s) => (
            <div key={s.key} className={cn("lp-card rounded-2xl p-4", s.currency && "col-span-2 md:col-span-1")} data-testid={`landing-kpi-${s.key}`}>
              <div className={cn("font-mono font-semibold tracking-tight", s.currency ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl")}><Counter value={Number(data[s.key] ?? 0)} currency={s.currency} ar={ar} /></div>
              <div className="mt-1 text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ----------------------------------- faq ----------------------------------- */

export function FaqSection() {
  const { c } = useCopy();
  const onOpen = (value) => { if (value) track("landing_faq_open", { index: Number(value.slice(1)) + 1 }); };
  return (
    <section id="faq" className="scroll-mt-20 bg-surface-2 py-16 sm:py-20" data-testid="landing-faq-section">
      <Container className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.5fr]">
        <Reveal><SectionHeading eyebrow={c.faq.eyebrow} title={c.faq.title} /></Reveal>
        <Reveal delay={0.05}>
          <Accordion type="single" collapsible className="lp-card rounded-2xl px-5" onValueChange={onOpen} data-testid="landing-faq-accordion">
            {c.faq.items.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} data-testid={`landing-faq-item-${i + 1}`}>
                <AccordionTrigger className="min-h-[44px] text-start text-sm font-semibold sm:text-base" data-testid={`landing-faq-trigger-${i + 1}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="lp-body text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Container>
    </section>
  );
}

/* -------------------------------- final CTA -------------------------------- */

export function FinalCta() {
  const { c, ar } = useCopy();
  const Arrow = ar ? ArrowLeft : ArrowRight;
  return (
    <section className="py-16 sm:py-20" data-testid="landing-cta-section">
      <Container>
        <Reveal>
          <div className="lp-card lp-cta relative isolate overflow-hidden rounded-2xl p-8 sm:p-12">
            <div className="noise-overlay absolute inset-0 opacity-40" aria-hidden />
            <div className="absolute -end-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" aria-hidden />
            <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-[1.15fr_1fr]">
              <div>
                <h2 className="lp-display text-2xl font-semibold tracking-[-0.01em] sm:text-3xl" data-testid="landing-cta-title">{c.closing.title}</h2>
                <p className="lp-body mt-2 text-base leading-7 text-muted-foreground">{c.closing.body}</p>
                <Reducers items={c.closing.reducers} className="mt-4" testId="landing-cta-reducers" />
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Button asChild size="lg" className="h-12 rounded-xl px-6 shadow-[var(--shadow-sm)] transition-shadow duration-150 hover:shadow-[var(--shadow-md)]" data-testid="landing-footer-cta-button"><Link to="/auth?mode=register" onClick={() => track("landing_cta_click", { placement: "footer" })}>{c.closing.cta}<Arrow size={16} className="ms-2" /></Link></Button>
                <Button asChild size="lg" variant="outline" className="h-12 rounded-xl bg-card/70 px-6" data-testid="landing-footer-model-holding-button"><Link to="/model" onClick={() => track("landing_secondary_click", { placement: "footer" })}>{c.closing.secondary}</Link></Button>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

/* --------------------------------- footer ---------------------------------- */

function FooterColumn({ heading, children, testId, className }) {
  return <div className={className} data-testid={testId}><h3 className="lp-eyebrow mb-3 text-muted-foreground">{heading}</h3><ul className="space-y-2 text-sm">{children}</ul></div>;
}

const footerLink = "rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Dense, honest footer (committee R-12): identity line, the record-not-registry statement, product / legal / contact columns, language switch. */
export function LandingFooter({ home = true }) {
  const { c, ar } = useCopy();
  const toggleLang = useLandingLanguageToggle();
  const anchor = (href) => (home ? href : `${ar ? "/ar" : "/"}${href}`);
  return (
    <footer className="border-t border-border bg-surface-2 pb-10 pt-12" data-testid="landing-footer-section">
      <Container>
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-10">
          <div className="col-span-2 max-w-md lg:col-span-1">
            <div className="flex items-center gap-2"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Landmark size={14} /></span><span className="text-sm font-semibold">Tamreer · <span className="font-ar">تمرير</span></span></div>
            <p className="mt-3 text-sm text-muted-foreground" data-testid="landing-footer-note">{c.footer.note}</p>
            <p className="mt-3 text-sm font-medium" data-testid="landing-footer-legal-statement">{c.footer.legal}</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-testid="landing-footer-software-statement">{c.footer.software}</p>
          </div>
          <FooterColumn heading={c.footer.productHeading} testId="landing-footer-product">
            {c.footer.productLinks.map((l) => <li key={l.id}><SmartLink to={l.to} href={l.href ? anchor(l.href) : undefined} className={footerLink} data-testid={`landing-footer-product-${l.id}`}>{l.label}</SmartLink></li>)}
          </FooterColumn>
          <FooterColumn heading={c.footer.legalHeading} testId="landing-footer-legal">
            {c.footer.links.map((l) => <li key={l.id}><Link to={l.to} className={footerLink} data-testid={`landing-footer-link-${l.id}`}>{l.label}</Link></li>)}
          </FooterColumn>
          <FooterColumn heading={c.footer.contactHeading} testId="landing-footer-contact" className="col-span-2 sm:col-span-1">
            <li>
              <div className="flex items-center gap-2 text-sm font-medium"><Mail size={14} className="text-primary" />{c.footer.complaints}</div>
              <a href={`mailto:${c.footer.complaintsEmail}`} className={cn(footerLink, "mt-1 block")} dir="ltr" data-testid="landing-footer-complaints-email">{c.footer.complaintsEmail}</a>
              <p className="mt-1 text-xs text-muted-foreground">{c.footer.complaintsNote}</p>
            </li>
            <li className="pt-2">
              <button type="button" onClick={toggleLang} className={cn(footerLink, "inline-flex min-h-11 items-center gap-2 sm:min-h-0")} data-testid="landing-footer-language-toggle" aria-label={c.footer.languageHint}>
                <Languages size={14} className="text-primary" /><span className={ar ? "" : "font-ar"}>{c.footer.languageLabel}</span>
              </button>
            </li>
          </FooterColumn>
        </div>
        <div className="mt-10 flex flex-col items-start justify-between gap-2 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <span dir="ltr">© {new Date().getFullYear()} Tamreer</span>
          <span>{c.header.tagline}</span>
        </div>
      </Container>
    </footer>
  );
}
